import { FormControl, FormGroup } from '@angular/forms';
import { EQueryCloseReasonsStorage } from '../e-query-close-reasons-storage.service';
import { REASONS_STORAGE_TOKEN } from '../../../audit-trail/audit-trail-reason.component';
import { EQuerySummaryStorage } from '../e-query-summary/e-query-summary-storage';
import { FileFormArray } from '../../../covance-file-upload/file-form-array';
import { FileToLanguage } from '../../../covance-file-upload/covance-file-upload.component';
import { EQueryReopenData } from '../models/e-query-reopen-data';
import { Settings } from '../../../core';
import { ChangeDetectionStrategy, Component, InjectionToken, ViewChild } from '@angular/core';
import { MdlDialogService } from '@angular-mdl/core';
import { CoordinatorEQueryDetailsStorage } from './coordinator-e-query-details-storage';
import {map} from 'rxjs/operators';
import {routeParams} from '../../../shared/route-helpers';
import { Observable, combineLatest } from 'rxjs';
import {CoordinatorEQuery} from '../models/e-query-details/coordinator-e-query';
import {CoordinatorEQueryEscalations} from '../models/e-query-details/coordinator-e-query-escalations';
import {ActivatedRoute} from '@angular/router';
import {GlobalPopupComponent} from '../../../global-popup';
import {EQueryAttachmentsDownloader} from '../../../e-queries/e-query-details/e-query-attachments-downloader';
import {EQueryCCQuestion} from '../models/e-query-c-c-question';
import {EQuerySubmittedResponse} from '../../../e-queries/models/e-query-submitted-response';
import {EQueryAttachment} from '../../../e-queries/models/e-query-attachment';
import { UserPermissionsStorage } from '../../../user-permissions/user-permissions-storage';
import { EQueryAccessionInfo } from '../../shared/e-query-accession-info';
import { maxLengthInBytes } from '../../../shared/validators';

export const COORDINATOR_EQUERY_DETAILS_TOKEN = new InjectionToken('EQueryDetails');

@Component({
    selector: 'coordinator-e-query-details-dialog',
    template: require('./coordinator-e-query-details-dialog.component.html'),
    styles: [require('./coordinator-e-query-details-dialog.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {provide: REASONS_STORAGE_TOKEN, useExisting: EQueryCloseReasonsStorage}
    ]
})
export class CoordinatorEQueryDetailsDialogComponent {
    public readonly auditTrailReasonCtrl: FormControl;
    public readonly eQueryTextCtrl: FormControl;
    public readonly fileFormArray = new FileFormArray(false);
    public readonly editorForm: FormGroup;
    public readonly allowedExtensions: string[];
    public isDisabledClose: boolean;
    public isDisabledReopen: boolean;
    public selectedFiles: FileToLanguage;
    public isInvalid = false;
    public canBeReopened$: Observable<boolean>;
    public canBeClosed$: Observable<boolean>;
    public showReason$: Observable<boolean>;
    public accessionData$: Observable<EQueryAccessionInfo>;
    public eQueryData$: Observable<CoordinatorEQuery>;
    public attachments$: Observable<EQueryAttachment[]>;
    public escalations$: Observable<CoordinatorEQueryEscalations>;
    public questions$: Observable<EQueryCCQuestion[]>;
    public responses$: Observable<EQuerySubmittedResponse[]>;
    public comments$: Observable<string>;
    public readonly filesMaxCount: number;
    public readonly eQueryMaxSizeOfAttachments: number;
    public readonly canUserRespond$: Observable<boolean>;
    public canUploadAttachments$: Observable<boolean>;

    private eQueryId: number;

    @ViewChild(GlobalPopupComponent)
    private popup: GlobalPopupComponent;
    
    constructor(
        private storage: CoordinatorEQueryDetailsStorage,
        private readonly route: ActivatedRoute,
        private readonly eQuerySummaryStorage: EQuerySummaryStorage,
        settings: Settings,
        private readonly mdlDialogService: MdlDialogService,
        private readonly eQueryAttachmentsDownloader: EQueryAttachmentsDownloader,
        permissionsStorage: UserPermissionsStorage,
    ) {
        this.canUserRespond$ = permissionsStorage.userPermissions.pipe(
            map(permissions => permissions.has('EQueryCreationRespondClose')),
        );
        this.eQueryDetailsCreation();
        this.allowedExtensions = settings.eQueryAttachmentsExtensions;
        this.filesMaxCount = settings.eQueryMaxNumberOfAttachmentsReopenClosed;
        this.eQueryMaxSizeOfAttachments = settings.eQueryMaxSizeOfAttachments;
        this.editorForm = new FormGroup({});
        this.auditTrailReasonCtrl = new FormControl('');
        this.editorForm.addControl('auditTrailReason', this.auditTrailReasonCtrl);
        this.editorForm.addControl('files', this.fileFormArray);
        this.eQueryTextCtrl = new FormControl('', [maxLengthInBytes(2050)]);
    }

    public async closeEQuery() {
        const result = await this.showConfirmDialog('The eQuery will be closed. Please note that this action cannot be undone. Proceed?');
        if (this.editorForm.invalid || !result) {
            return;
        }
        try {
            this.isDisabledClose = true;
            await this.storage.closeEQuery(this.eQueryId, this.auditTrailReasonCtrl.value);
            this.eQuerySummaryStorage.forceLoadEQueries();
        } finally {
            this.isDisabledClose = false;
            this.close();
        }
    }

    public close() {
        this.popup.onBackToPortal();
    }

    public onFilesChanged(selectedFiles: FileToLanguage) {
        this.selectedFiles = selectedFiles;
    }

    public async reopen() {
        const result = await this.showConfirmDialog('Do you want to reopen this eQuery?');
        if (this.editorForm.invalid || !result) {
            return;
        }
        try {
            this.isDisabledReopen = true;
            const data = new EQueryReopenData(this.auditTrailReasonCtrl.value, this.eQueryTextCtrl.value, this.selectedFiles);
            await this.storage.reopenEQuery(this.eQueryId, data);
            this.eQuerySummaryStorage.forceLoadEQueries();
        } finally {
            this.isDisabledReopen = false;
            this.close();
        }
    }

    public async downloadAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.downloadCoordinatorAttachment(attachmentId, this.eQueryId);
    }

    public async previewAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.previewInvestigatorAttachment(attachmentId, this.eQueryId);
    }

    public async downloadResponseAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.downloadResponseAttachment(attachmentId);
    }

    public async previewResponseAttachment(attachmentId: number): Promise<boolean> {
        return this.eQueryAttachmentsDownloader.previewResponseAttachment(attachmentId);
    }

    private eQueryDetailsCreation(): void {
        const eQueryId$ = this.route.params.pipe(map(x => {
            const eQueryId = x[routeParams.eQueryId] as string | undefined;
            return this.eQueryId = parseInt(eQueryId, 10);
        }));
        const eQueryDetails$ = this.storage.getEQueryDetails(eQueryId$);

        this.eQueryData$ = eQueryDetails$.pipe(map(x => x.eQueryData));
        this.canBeReopened$ = eQueryDetails$.pipe(map(x => x.eQueryData.canBeReopened));
        this.canBeClosed$ = eQueryDetails$.pipe(map(x => x.eQueryData.canBeClosed));
        this.showReason$ = combineLatest([
            this.canUserRespond$,
            eQueryDetails$
        ]).pipe(
            map(([canUserRespond, x]) => canUserRespond && (x.eQueryData.canBeClosed || x.eQueryData.canBeReopened)),
        );
        this.accessionData$ = eQueryDetails$.pipe(map(x => x.accessionData));
        this.attachments$ = eQueryDetails$.pipe(map(x => x.attachments));
        this.comments$ = eQueryDetails$.pipe(map(x => {
            return x.eQueryData.commentText;
        }));
        this.escalations$ = eQueryDetails$.pipe(map(x => x.emailEscalations));
        this.questions$ = eQueryDetails$.pipe(map(x => x.questions));
        this.responses$ = eQueryDetails$.pipe(map(x => x.responseDetails));
        this.canUploadAttachments$ = combineLatest([
            this.canUserRespond$,
            this.canBeReopened$,
        ]).pipe(
            map(([canUserRespond, canBeReopened]) => canUserRespond && canBeReopened),
        );
    }

    private async showConfirmDialog(confirmation: string): Promise<boolean> {
        try {
            await this.mdlDialogService.confirm(confirmation).toPromise();
            return true;
        } catch {
            return false;
        }
    }
}
